import React, { useMemo, useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import * as S from "./ListOfAlerts.Style";
import { Actions } from "../../../../../utils/actionsIds";

// Pipes
import * as P from "../../../../../utils/pipes";
import Popup from "reactjs-popup";
import ZaiaToggleCircular from "../../../../zaiaComponents/zaiaToggleCircular";
import ZaiaImage from "../../../../zaiaComponents/zaiaImage";
import ZaiaReaction from "../../../../zaiaComponents/zaiaReaction";
import ZaiaSelect from "../../../../zaiaComponents/zaiaSelect";
import { PopUpPayload } from "../../../outcomes/components/outcomesComponents/shared/genericTemplate/GenericTemplate.style";

// Assets
const logoZaiaLoading = `${process.env.REACT_APP_STORAGE_ZAIA_RESOURCES}/zaiaReactions/singularity-health-loader.svg`;
const logoZaiaAlert = `${process.env.REACT_APP_STORAGE_ZAIA_RESOURCES}/zaiaReactions/reaction-error-data.svg`;
const logoZaiaEmpty = `${process.env.REACT_APP_STORAGE_ZAIA_RESOURCES}/zaiaReactions/reaction-no-alerts.svg`;
const alertsEmpty = `${process.env.REACT_APP_STORAGE_ZAIA_RESOURCES}/zaiaReactions/list-alerts-empty.svg`;
const feather = `${process.env.REACT_APP_STORAGE_ZAIA_RESOURCES}/zaiaFigures/feather.svg`;

function ListOfAlerts({
  selectPatient,
  alertsList,
  alertsListSelected,
  setAlertsListSelected,
  allAlertsSelected,
  setAllAlertsSelected,
  alertsListLoading,
  alertsListError,
  pendingAlertsActived,
  setPendingAlertsActived,
  historyAlertActive,
  setHistoryAlertActive
}) {

  const [alertListFiltered, setAlertListFiltered] = useState([]);
  const [filterAZ, setFilterAZ] = useState(null);
  const [filterDate, setFilterDate] = useState(null);
  const [filterAlertLevel, setFilterAlertLevel] = useState([]);
  const [filterAlertType, setFilterAlertType] = useState([]);
  const [commentsOnly, setCommentsOnly] = useState(false);
  const [managementStatusSelected, setManagementStatusSelected] = useState(null);  

  const {
    t,
    i18n: { language },
  } = useTranslation("global");

  const managementStatus = [
    {
      value: null,
      text: t("globally.all"),
      icon: "zaia-i-check-1"
    }, 
    {
      value: "managed",
      text: t("alerts.managed"),
      icon: "zaia-i-check-1"
    },
    {
      value: "archived",
      text: t("alerts.archived"),
      icon: "zaia-i-close"
    },
    {
      value: "notAnAlert",
      text: t("alerts.notAnAlert"),
      icon: "zaia-i-close"
    }
  ]

  const alertLevels = [
    {
      id: 1,
      level: 1,
      name: `${t("globally.level")} 1`,
      color: "#34BFAA"
    },
    {
      id: 2,
      level: 2,
      name: `${t("globally.level")} 2`,
      color: "#34BFAA"
    },
    {
      id: 3,
      level: 3,
      name: `${t("globally.level")} 3`,
      color: "#34BFAA"
    },
    {
      id: 4,
      level: 4,
      name: `${t("globally.level")} 4`,
      color: "#34BFAA"
    },
    {
      id: 5,
      level: 5,
      name: `${t("globally.level")} 5`,
      color: "#34BFAA"
    },
    {
      id: 6,
      level: 1,
      name: t("globally.belowRank"),
      color: "#7551E2"
    },
    {
      id: 7,
      level: 2,
      name: t("globally.aboveRank"),
      color: "#7551E2"
    },
    {
      id: 8,
      level: 4,
      name: t("globally.comment"),
      color: "#3687F0"
    }
  ]

  const alertTypes = [
    {
      id: 1,
      name: t("globally.symptom"),
      icon: "zaia-icono-sintomas",
      type: "sideEffect"
    },
    {
      id: 2,
      name: t("globally.healthIndicator"),
      icon: "zaia-icono-signos-vitales-relleno",
      type: "vitalSign"
    },
    {
      id: 3,
      name: t("globally.medication"),
      icon: "zaia-icono-medicina-relleno",
      type: "drug"
    }
  ]   

  const applyFilters = useCallback(() => {
    let filteredList = alertsList;
  
    if (filterAZ !== null) {
      filteredList = [...filteredList].sort((a, b) => {
        const nameA = `${a.user.name} ${a.user.lastname}`;
        const nameB = `${b.user.name} ${b.user.lastname}`;
        return !filterAZ ? nameB.localeCompare(nameA) : nameA.localeCompare(nameB);
      });
    }
  
    if (filterDate !== null) {
      filteredList = [...filteredList].sort((a, b) =>
        filterDate
          ? new Date(b.created_at) - new Date(a.created_at)
          : new Date(a.created_at) - new Date(b.created_at)
      );
    }

    if(filterAlertLevel.length > 0) {

      filteredList = filteredList?.filter((alert) => {

        if(filterAlertLevel.includes(6) && filterAlertLevel.includes(7)) {
          return alert.alertTypeId === "vitalSign" && (alert.alertSeverity === 1 || alert.alertSeverity === 2)
        }

        if(filterAlertLevel.includes(6)) {
          return alert.alertTypeId === "vitalSign" && alert.alertSeverity === 1
        } 
        
        if(filterAlertLevel.includes(7)) {
          return alert.alertTypeId === "vitalSign" && alert.alertSeverity === 2
        } 

        return filterAlertLevel.includes(alert.sideEffectSeverity) && alert.alertSeverity !== null
      })

    }

    if(filterAlertType.length > 0) {
      
      filteredList = filteredList.filter((alert) => {
        return filterAlertType.includes(alert.alertTypeId)
      })
    }

    if(commentsOnly) {
      filteredList = filteredList.filter((alert) => {
        return alert.alertSeverity === null
      })
    }

    if(managementStatusSelected && !pendingAlertsActived) {      
      filteredList = filteredList.filter((alert) => {
        return alert.alertStatusId === managementStatusSelected
      })
    }

    setAlertListFiltered(filteredList);
  }, [filterAZ, filterDate, filterAlertLevel, filterAlertType, commentsOnly, managementStatusSelected, pendingAlertsActived, alertsList]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const selectAlert = (item, option) => {
    if(option) {
      setAlertsListSelected(alertsListSelected.filter((alert) => alert !== item.id))
    } else {
      setAlertsListSelected([...alertsListSelected, item.id])
    }
  } 

  const getSeverityAlertText = (type ,severity, sideEffectSeverity) => {
    if(type === "sideEffect" && severity) {
      switch (sideEffectSeverity) {
        case 1:
          return {
            text: alertLevels[0]?.name,
            color: alertLevels[0]?.color
          };
        case 2:
          return {
            text: alertLevels[1]?.name,
            color: alertLevels[1]?.color
          };
        case 3:
          return {
            text: alertLevels[2]?.name,
            color: alertLevels[2]?.color
          };
        case 4:
          return {
            text: alertLevels[3]?.name,
            color: alertLevels[3]?.color
          };
        case 5:
          return {
            text: alertLevels[4]?.name,
            color: alertLevels[4]?.color
          };
        default:
          return {
            text: alertLevels[0]?.name,
            color: alertLevels[0]?.color
          };
      }
    } else {
      switch (severity) {
        case 1:
          return {
            text: alertLevels[5]?.name,
            color: alertLevels[5]?.color
          };
        case 2:
          return {
            text: alertLevels[6]?.name,
            color: alertLevels[6]?.color
          };
        case null:
          return {
            text: alertLevels[7]?.name,
            color: alertLevels[7]?.color
          };
        default:
          return {
            text: alertLevels[6]?.name,
            color: alertLevels[6]?.color
          };
      }
    }
  } 

  const calcCurrentPressure = (pSis, pDias) => {
    return ((Number(pSis) + (2*Number(pDias)))/3)?.toFixed(2);
  }

  return (
    <S.ListOfAlerts>
      <div className="table-container">
        {
          (filterAlertLevel.length > 0 ||
          filterAlertType.length > 0 ||
          commentsOnly ) &&
          <div className="filters">
            <div className="filters__left">
              <div className="filters__label">
                {t("alerts.filtering")}
              </div>
              {
                filterAlertLevel.length > 0 &&
                <div className="filter-container">
                  <button className="filter-container__close"
                    onClick={() => setFilterAlertLevel([])}
                  >
                    <i className="filter-container__close-icon icon zaia-icono-cerrar"></i>
                  </button>
                  <div className="filter-container__label">
                    {t("alerts.alertLevel")}
                  </div>
                  {
                    filterAlertLevel.map((level) => {
                      return (
                        <div 
                          className="filter-container__item"
                          key={level}
                        >
                          <button className="filter-container__item-icon"
                            onClick={() => setFilterAlertLevel(filterAlertLevel.filter((item) => item !== level))}
                          >
                            <i className="icon zaia-icono-cerrar"></i>
                          </button>
                          <div className="filter-container__item-text">
                            {alertLevels.find((item) => item.id === level)?.name}
                          </div>
                          <div className="filter-container__item-point"
                            style={{
                              backgroundColor: alertLevels.find((item) => item.id === level)?.color
                            }}
                          ></div>
                        </div>
                      )
                    })
                  }
                </div>
              }
              {
                filterAlertType.length > 0 &&
                <div className="filter-container">
                  <button className="filter-container__close"
                    onClick={() => setFilterAlertType([])}
                  >
                    <i className="filter-container__close-icon icon zaia-icono-cerrar"></i>
                  </button>
                  <div className="filter-container__label">
                    {t("alerts.alertType")}
                  </div>
                  {
                    filterAlertType.map((type) => {
                      return (
                        <div 
                          className="filter-container__item"
                          key={type}
                        >
                          <button className="filter-container__item-icon"
                            onClick={() => setFilterAlertType(filterAlertType.filter((item) => item !== type))}
                          >
                            <i className="icon zaia-icono-cerrar"></i>
                          </button>
                          <div className="filter-container__item-icon-type">
                            <i className={`icon ${
                              alertTypes.find((item) => item.type === type)?.icon
                            }`}></i>
                          </div>
                          <div className="filter-container__item-text">
                            {alertTypes.find((item) => item.type === type)?.name}
                          </div>
                        </div>
                      )
                    })
                  }
                </div>
              }
              {
                commentsOnly &&
                <div className="filter-container">
                  <button className="filter-container__close"
                    onClick={() => setCommentsOnly(false)}
                  >
                    <i className="filter-container__close-icon icon zaia-icono-cerrar"></i>
                  </button>
                  <div className="filter-container__label">
                    {t("globally.comments")}
                  </div>
                </div>
              }
            </div>
            <div className="filters__right">
              <div className="filter-container">
                <button className="filter-container__close"
                  onClick={() => {
                    setFilterAlertLevel([]);
                    setFilterAlertType([]);
                    setCommentsOnly(false);
                  }}
                >
                  <i className="filter-container__close-icon icon zaia-icono-cerrar"></i>
                </button>
                <div className="filter-container__label">
                  {t("globally.cleanFilters")}
                </div>
              </div>
            </div>
          </div>
        }
        <table className="suggestion-table">
          <thead className="suggestion-table__head">
            <tr>
              <th className="filter filter--patient-name"> 
                <div className="filter__container">
                  {
                    !allAlertsSelected 
                      ? <i className="check-patient icon zaia-i-checkbox-off"
                        onClick={() => {
                          setAllAlertsSelected(true);
                          setAlertsListSelected(alertsList.map(alert => alert.id));
                        }}
                        ></i>
                        : <i className="check-patient icon zaia-i-checkbox-on"
                        onClick={() => {
                          setAllAlertsSelected(false);
                          setAlertsListSelected([]);
                        }}
                      ></i>
                  }
                  <div className="filter__button-content filter__button-content--name">
                    <div 
                      className={`filter__button filter__button--name ${filterAZ !== null && "filter__button--actived"}`}
                      onClick={() => setFilterAZ(
                        filterAZ === null ? true : (filterAZ === false ? null : false)
                      )}
                    >
                      <i className={`filter__icon icon zaia-i-name-sort`}></i>
                      <div className="filter__text">
                        {t("globally.name")}
                      </div>
                    </div>
                    <div className="filter__label-actived filter__label-actived--name">
                      {
                        filterAZ === null
                        ? ""
                        : filterAZ === false 
                          ? `${t("aiNavigator.filters.orderAZ")} Z-A`
                          : `${t("aiNavigator.filters.orderAZ")} A-Z`
                      }
                    </div>
                  </div>
                </div>
              </th>
              <th className="filter filter--program">
                <Popup
                  trigger={
                    <div className={`filter__button ${filterDate !== null && "filter__button--actived"}`}>
                      <i className="filter__icon icon zaia-i-especilidad-sort"></i>
                      <div className="filter__text">
                        {t("alerts.alertLevel")}
                      </div>
                    </div>
                  }
                  closeOnDocumentClick
                  keepTooltipInside
                  position={["bottom center"]}
                  nested
                >
                  {
                    close => (
                      <FilterAlerts
                        close={close}
                        filterAlertLevel={filterAlertLevel}
                        setFilterAlertLevel={setFilterAlertLevel}
                        filterAlertType={filterAlertType}
                        setFilterAlertType={setFilterAlertType}
                        commentsOnly={commentsOnly}
                        setCommentsOnly={setCommentsOnly}
                        alertLevels={alertLevels}
                        alertTypes={alertTypes}
                        alertListFiltered={alertListFiltered}
                        alertsList={alertsList}
                      />
                    )
                  }
                </Popup>                   
              </th>
              <th className="filter filter--program">
                <Popup
                  trigger={
                    <div className={`filter__button ${filterDate !== null && "filter__button--actived"}`}>
                      <i className="filter__icon icon zaia-i-especilidad-sort"></i>
                      <div className="filter__text">
                        {t("alerts.alertType")}
                      </div>
                    </div>
                  }
                  closeOnDocumentClick
                  keepTooltipInside
                  position={["bottom center"]}
                  nested
                >
                  {
                    close => (
                      <FilterAlerts
                        close={close}
                        filterAlertLevel={filterAlertLevel}
                        setFilterAlertLevel={setFilterAlertLevel}
                        filterAlertType={filterAlertType}
                        setFilterAlertType={setFilterAlertType}
                        commentsOnly={commentsOnly}
                        setCommentsOnly={setCommentsOnly}
                        alertLevels={alertLevels}
                        alertTypes={alertTypes}
                        alertListFiltered={alertListFiltered}
                        alertsList={alertsList}
                      />
                    )
                  }
                </Popup>                   
              </th>
              <th className="filter">
                <div className="filter__button">
                  <i className="filter__icon filter__icon-description icon zaia-icono-menu"/>
                  <div className="filter__text">{
                    t("alerts.alertDescription")
                  }</div>
                </div>
              </th>
              <th className="filter filter--date">
                <div className="filter__container">
                  <div className="filter__button-content">
                    <div 
                      className={`filter__button ${filterDate !== null && "filter__button--actived"}`}
                      onClick={() => setFilterDate(
                        filterDate === null ? true : (filterDate === false ? null : false)
                      )}
                    >
                      <i className="filter__icon icon zaia-i-name-sort"></i>
                      <div className="filter__text">
                        {t("globally.date")}
                      </div>
                    </div>
                    <div className="filter__label-actived">
                      {
                        filterDate === null
                        ? ""
                        : filterDate === true
                          ? t("aiNavigator.filters.mostRecent")
                          : t("aiNavigator.filters.oldest")
                      }
                    </div>
                  </div>
                </div>
              </th>
              <th>
                {
                  !pendingAlertsActived &&
                  <div className="management-status-selector">
                    <ZaiaSelect
                      type="select"
                      setValue={(e) => {
                        setManagementStatusSelected(e)
                      }}
                      value={managementStatusSelected}
                      items={managementStatus.map(
                        (status) => {
                          return {
                            value: status?.value,
                            text: status?.text
                          }
                        }
                      )}
                      shadow={false}
                      placeholder={t("alerts.managementStatus")}
                      fontSize="14px"
                      backgroundColor={"#F0F0FF"}
                      borderRadius={"31px"}
                      heightList={"200px"}
                      color={"#36369B"}
                      textColor={"#36369B"}
                      arrowColor={"#36369B"}
                      borderColor={"transparent"}
                      textItemsColor={"#36369B"}
                      placeholdertextcolor={"#36369B"}
                    />
                  </div>
                }
              </th>
            </tr>
          </thead>
          <tbody className="suggestion-table__body">
            { 
              alertListFiltered?.map((alert) => {

                return (
                  <tr 
                    key={alert.id}
                    className={`suggestion-table__item ${alertsListSelected.includes(alert.id) && "suggestion-table__item--selected"}`}>
                    <td className="patient-info">
                      {
                        alertsListSelected.includes(alert.id)
                        ? <i className="check-patient icon zaia-i-checkbox-on"
                          onClick={
                            () => {selectAlert(alert, true)}
                          }
                        ></i>
                        : <i className="check-patient icon zaia-i-checkbox-off"
                          onClick={
                            () => {selectAlert(alert, false)}
                          }
                        ></i>
                      }
                      <div className="patient-content">
                        <ZaiaImage
                          img={alert.user.picture}
                          alt=""
                          size={31}
                          rounded={true}
                        />
                        <div className="patient-content__info">
                          {
                            alertsListSelected.includes(alert.id) &&
                            <div className="patient-content__alert-selected">
                              <div className="patient-content__alert-selected">{t("alerts.selectedAlert")}</div>
                            </div>
                          }
                          <div className="patient-content__info-name-psa">
                            {alert.user.name + " " + alert.user.lastname}
                          </div>
                          {
                            <div className="patient-content__extra-option"
                              onClick={() => {
                                selectPatient(alert.user)
                              }}
                            >
                              <div className="patient-content__extra-option-text">{t("alerts.filterByThisPatient")}</div>
                              <i className="patient-content__extra-option-icon icon zaia-i-especilidad-sort"></i>
                            </div>
                          }
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="alert-level"
                        style={{
                          backgroundColor: getSeverityAlertText(alert.alertTypeId, alert.alertSeverity, alert.sideEffectSeverity).color
                        }}
                      >
                        {
                          alert.alertSeverity !== null &&
                          <Popup
                            trigger={
                              <i className="alert-level__icon icon zaia-i-tooltip"></i>
                            }
                            on={["hover", "focus"]}
                            closeOnDocumentClick
                            keepTooltipInside
                            position={["bottom center"]}
                            nested
                          >
                            <PopUpPayload>
                              {
                                alert.alertTypeId === "sideEffect" && alert.alertSeverity &&
                                <>
                                  {t("alerts.alertSeverityTooltip1") + ' '} 
                                  <b>
                                    {
                                      getSeverityAlertText(alert.alertTypeId, alert.alertSeverity, alert.sideEffectSeverity).text 
                                    }  
                                  </b>
                                  {' ' + t("alerts.alertSeverityTooltip2symptom") + ' '} 
                                  <b>
                                    {
                                      P.firstLetterUpper(P.dataTranslation(alert.sideEffectName, language))
                                    }
                                  </b>
                                  {' ' + t("alerts.alertSeverityTooltip3") + ' '} 
                                  <b>
                                    {
                                      P.dataTranslation(alert.processName, language)
                                    }
                                  </b>
                                </>
                              }
                              {
                                alert.alertTypeId === "vitalSign" && alert.alertSeverity &&
                                <>
                                  {t("alerts.alertSeverityTooltip1") + ' '} 
                                  <b>
                                    {
                                      getSeverityAlertText(alert.alertTypeId, alert.alertSeverity, alert.sideEffectSeverity).text 
                                    }  
                                  </b>
                                  {' ' + t("alerts.alertSeverityTooltip2vitalSign") + ' '} 
                                  <b>
                                    {
                                      P.firstLetterUpper(P.dataTranslation(alert.vitalSignName, language))
                                    }
                                  </b>
                                  {' ' + t("alerts.alertSeverityTooltip3") + ' '} 
                                  <b>
                                    {
                                      P.dataTranslation(alert.processName, language)
                                    }
                                  </b>
                                </>
                              }
                            </PopUpPayload>
                          </Popup>
                        }
                        <div className="alert-level__text">{getSeverityAlertText(alert.alertTypeId, alert.alertSeverity, alert.sideEffectSeverity).text}</div>
                      </div>
                    </td>
                    <td>
                      <div className="alert-type">
                          <i className={`alert-type__icon icon ${
                            alertTypes.find((item) => item.type === alert.alertTypeId)?.icon
                          }`}></i>
                          <div className="alert-type__text">{
                            alertTypes.find((item) => item.type === alert.alertTypeId)?.name
                          }</div>
                        </div>
                      </td>
                    <td>
                      {
                        alert.alertSeverity === null &&
                        <>
                          {
                            alert.alertTypeId === "sideEffect" &&
                            <div className="alert-description">
                              {
                                alert.sideEffectSeverityDescription &&
                                <Popup
                                  trigger={
                                    <div className="alert-description__icon icon zaia-i-tooltip"></div>
                                    
                                  }
                                  on={["hover", "focus"]} 
                                  closeOnDocumentClick
                                  keepTooltipInside
                                  position={["bottom center"]}
                                  nested
                                >
                                  <PopUpPayload>
                                    {
                                      P.dataTranslation(alert.sideEffectSeverityDescription, language)
                                    }
                                  </PopUpPayload>
                                </Popup>
                              }
                              <div className="alert-description__text">
                                <b>{P.firstLetterUpper(P.dataTranslation(alert.sideEffectName, language))}:</b>
                              </div>
                            </div>
                          }
                          {
                            alert.alertTypeId === "drug" &&
                            <div className="alert-description">
                              <div className="alert-description__text">
                                <b>{alert.drugName}:</b>
                              </div>
                            </div>
                          }
                          <div className="alert-description">                          
                            <div className="alert-description__text">
                              {
                                alert.userComment?.split(" ").length > 10
                                ? `"${alert.userComment?.split(" ").slice(0, 7).join(" ")}... `
                                : `"${alert.userComment}"`
                              } 
                              <Popup
                                trigger={
                                  <t>
                                    <b className="expand-comment">{
                                      alert.userComment?.split(" ").length > 10 && t("globally.readMore")
                                    }</b>
                                  </t>
                                }
                                keepTooltipInside
                                closeOnDocumentClick
                                position={["bottom"]}
                                on={["click"]}
                                nested
                              >
                                <S.PopUpPayload>
                                  "{
                                    alert.userComment
                                  }"
                                </S.PopUpPayload>
                              </Popup>
                            </div>
                          </div>
                        </>
                      }
                      {
                        alert.alertTypeId === "sideEffect" && alert.alertSeverity &&
                        <div className="alert-description">
                          {
                            alert.sideEffectSeverityDescription &&
                            <Popup
                              trigger={
                                <div className="alert-description__icon icon zaia-i-tooltip"></div>
                                
                              }
                              on={["hover", "focus"]} 
                              closeOnDocumentClick
                              keepTooltipInside
                              position={["bottom center"]}
                              nested
                            >
                              <PopUpPayload>
                                {
                                  P.dataTranslation(alert.sideEffectSeverityDescription, language)
                                }
                              </PopUpPayload>
                            </Popup>
                          }
                          <div className="alert-description__text">
                            <b>{P.firstLetterUpper(P.dataTranslation(alert.sideEffectName, language))}</b>
                          </div>
                        </div>
                      }
                      {
                        alert.alertTypeId === "vitalSign" && alert.alertSeverity &&
                        <div className="alert-description">
                          <div className="alert-description__text">
                            {P.dataTranslation(alert.vitalSignName, language)}  <b>{
                              (
                                alert.vitalSignName?.en === "Blood Pressure"
                                ? calcCurrentPressure(alert.measure.measure1, alert.measure.measure2)
                                : alert.measure.measure1
                              )
                              + alert.measure.unit
                            }</b>
                          </div>
                        </div>
                      }
                    </td>
                    <td>
                      <div className="alert-date">
                        {P.longDate(alert.created_at, t)}
                      </div>
                    </td>
                    <td>
                      <div className="alert-management">
                        {
                          alert.alertStatusId === "managed" &&
                          <div className="alert-management__status">
                            <div className="alert-management__status-text">{t("alerts.managed")}</div>
                            <i className="alert-management__status-icon icon zaia-i-visto"></i>
                          </div>
                        }
                        {
                          alert.alertStatusId === "notAnAlert" &&
                          <div className="alert-management__status">
                            <div className="alert-management__status-text">{t("alerts.notAnAlert")}</div>
                            <i className="alert-management__status-icon icon zaia-i-alerts"></i>
                          </div>
                        }
                        {
                          alert.alertStatusId === "archived" &&
                          <div className="alert-management__status">
                            <div className="alert-management__status-text">{t("alerts.archived")}</div>
                            <i className="alert-management__status-icon icon zaia-i-alerts"></i>
                          </div>
                        }
                        {/* <Popup
                          trigger={
                            <div className="alert-options">
                              <i className="alert-options__icon zaia-icono-dots-vertical"></i>
                            </div>
                          }
                          closeOnDocumentClick
                          keepTooltipInside
                          position={["bottom center"]}
                          nested
                        >
                          {
                            close => (
                              <S.AlertsOptions>
                                <div className="header">
                                  Más acciones
                                </div>
                                <div className="options">
                                  <div className="option">
                                    <div className="option__title">
                                      Información del paciente
                                    </div>
                                  </div>
                                  <div className="option"
                                    onClick={() => {
                                      setHistoryAlertActive(alert)
                                    }}
                                  >
                                    <div className="option__title">
                                      Histórico de cambios de estados
                                      </div>
                                    <div className="option__subtitle">
                                      Cambio de estado de la alerta realizado 
                                    </div>
                                  </div>
                                </div>
                              </S.AlertsOptions>
                            )
                          }
                        </Popup> */}
                      </div>
                    </td>
                  </tr>
                )
              })
            }
          </tbody>
        </table>
        {
          alertsListError &&
          <div className="reaction">
            <div className="reaction__content">
              <ZaiaReaction
                zaiaImg={logoZaiaAlert}
                widthimg="100px"
                text={t("alerts.errorLoadingAlerts")}
                sizeText="15px"
                widthText="240px"
              />
            </div>
          </div>
        }
        {
          alertsListLoading &&
          <div className="reaction">
            <div className="reaction__content">
              <ZaiaReaction
                widthimg="150px"
                zaiaImg={logoZaiaLoading}
              />  
            </div>
          </div>
        }
        {
          (alertListFiltered?.length === 0 && pendingAlertsActived) &&
          <div className="reaction">
            <div className="no-alerts">
              <img className="no-alerts__img" src={alertsEmpty} alt="" />
              <div className="no-alerts__text">{t("alerts.theresNoAlerts")}</div>
              <button className="no-alerts__button"
                onClick={() => {setPendingAlertsActived(false)}}
              >{t("alerts.reviewManagementHistorial")}</button>
            </div>
          </div>
        }
      </div>
    </S.ListOfAlerts>
  );
}


const FilterAlerts = ({ 
  close,
  filterAlertLevel,
  setFilterAlertLevel,
  filterAlertType,
  setFilterAlertType,
  commentsOnly,
  setCommentsOnly,
  alertLevels,
  alertTypes,
  alertListFiltered,
  alertsList
}) => {  

  const {
    t,
    i18n: { language },
  } = useTranslation("global");
  
  return(
    <S.FiltersAlerts>
      <div className="filters-box filters-box--left">
        <div className="filters-box__label">{t("alerts.selectLevel")}</div>
        <div className="filters-box__title">{t("alerts.alertLevel")}</div>
        <div className="filters-box__content">
          <div className="levels-list">
            <div className={`level-item ${filterAlertLevel.length === 0 && "level-item--actived"}`}
              onClick={() => {
                setFilterAlertLevel([]);
              }}
            >
              <div className="level-item__point"
                style={{
                  backgroundColor: `${filterAlertLevel.length === 0 ? "white" : "#1F1F52"}`
                }}
              ></div>
              <div className="level-item__text">{t("globally.all")}</div>
              <div className="level-item__number">{'(' + alertsList?.length + ')'}</div>
            </div>
            {
              alertLevels?.slice(0, -1)?.map((level) => {
                return (
                  <div 
                    className={`level-item ${filterAlertLevel.includes(level.id) && "level-item--actived"}`}
                    key={level.id}
                    onClick={() => {
                      if(filterAlertLevel.includes(level.id)) {
                        setFilterAlertLevel(filterAlertLevel.filter((item) => item !== level.id))
                      } else {
                        setFilterAlertLevel([...filterAlertLevel, level.id])
                      }
                    }}
                  >
                    <div className="level-item__point"
                      style={{
                        backgroundColor: level.color
                      }}
                    ></div>
                    <div className="level-item__text">{level.name}</div>
                    <div className="level-item__number">{
                      '(' +
                        alertsList?.filter((alert) => {
                          
                          if(level.id <= 5 && alert.alertTypeId === "sideEffect" && alert.alertSeverity) {
                            return alert.sideEffectSeverity === level.level
                          }
                          
                          if(level.id > 5 && alert.alertTypeId === "vitalSign") {
                            
                            return alert.alertSeverity === level.level
                          }

                        }).length
                      + ')'
                    }</div>
                  </div>
                )
              })
            }
          </div>
        </div>
      </div>
      <div className="filters-box filters-box--right">
        <div className="filters-box__label">{t("alerts.selectType")}</div>
        <div className="filters-box__title">{t("alerts.alertType")}</div>
        <div className="filters-box__content">
          <div className="types-list">
            <div className="type-item"
              onClick={() => {setFilterAlertType([])}}
            >
              <div className={`type-item__circle ${filterAlertType?.length === 0 && "type-item__circle--actived"}`}>
                <div className={`type-item__circle-number ${filterAlertType?.length === 0 && "type-item__circle-number--actived"}`}>
                  {alertsList?.length}
                </div>
                <div className="type-item__circle-icon icon zaia-icono-home"></div>
              </div>
              <div className="type-item__label">{t("globally.all")}</div>
            </div>
            {
              alertTypes?.map((type) => {
                return (
                  <div className="type-item"
                    onClick={() => {
                      // add value to a filterAlertType if it exist delete it
                      if(filterAlertType.includes(type.type)) {
                        setFilterAlertType(filterAlertType.filter((item) => item !== type.type))
                      } else {
                        setFilterAlertType([...filterAlertType, type.type])
                      }
                    }}
                  >
                    <div className={`type-item__circle ${filterAlertType.includes(type.type) && "type-item__circle--actived"}`}>
                      <div className={`type-item__circle-number ${filterAlertType.includes(type.type) && "type-item__circle-number--actived"}`}>
                        {
                          alertsList?.filter((alert) => {
                            return alert.alertTypeId === type.type
                          }).length
                        }
                      </div>
                      <div className={`type-item__circle-icon icon ${type.icon}`}></div>
                    </div>
                    <div className="type-item__label">{type.name}</div>
                  </div>
                )
              })
            }
          </div>
          <div className="just-comments">
            <div className="just-comments__left">
              <div className="just-comments__icon icon zaia-i-message-outline"></div>
              <div className="just-comments__text">{t("alerts.justComments")}</div>
            </div>
            <div className="just-comments__right">
              <div className="just-comments__switch">
              <ZaiaToggleCircular
                id={'patient-alert-toggle'}
                color={"#27AE60"}
                toggle={(e) => {
                  setCommentsOnly(e)
                }}
                active={commentsOnly}
              />
              </div>
            </div>
          </div>
        </div>
      </div>
    </S.FiltersAlerts>
  )

}

export default ListOfAlerts;